import React, { useContext } from "react";
import { Link, Tooltip, TooltipProps } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { AppContext } from "../../context/app-context";
import { hasOwnerAccess } from "../../util/AccountUtils";

export const COMPANY_PROFILE_PATH = "/account/company-profile";

const DEFAULT_DISABLED_REASON =
  "You must confirm your email address before creating or editing locations.";

export const NON_OWNER_VERIFY_HINT = "Ask your account owner to verify the company email.";

// Rendered inline rather than portaled so keyboard users can Tab from the guarded
// control straight into the tooltip's link. Fixed positioning keeps the inline
// popper from being clipped by overflow-hidden ancestors (e.g. cards).
const POPPER_PROPS: TooltipProps["PopperProps"] = {
  disablePortal: true,
  popperOptions: { strategy: "fixed" },
};

// Theme body text, so tooltip copy is readable (MUI's default is ~10px against our 14px root).
const TOOLTIP_SLOT_PROPS: TooltipProps["slotProps"] = {
  tooltip: { sx: { typography: "body2" } },
};

interface EmailVerifyGuardTooltipProps {
  /** When `true`, the guard is active: the tooltip shows and the wrapped control is treated as disabled. */
  disabled: boolean;
  /** Message shown in the tooltip while disabled. Defaults to the standard email-confirmation reason. */
  disabledReason?: string;
  /** The control to guard — typically an MUI `Button`. */
  children: React.ReactElement<any>;
}

/**
 * Wraps an interactive control (e.g. a `Button`) so that, while `disabled` is
 * `true`, a tooltip explains why the action is unavailable and how to fix it.
 * Owners/admins get a link to the Company Profile (where the company email is
 * verified); other users are told to ask their account owner.
 *
 * MUI suppresses pointer events on disabled elements, so the control is wrapped
 * in a focusable `<span>` — without it the tooltip would never fire on
 * hover/focus. When `disabled` is `false` the children render unchanged.
 *
 * @example
 * <EmailVerifyGuardTooltip disabled={!isEmailVerified}>
 *   <Button onClick={openCreateModal}>Create Another Location</Button>
 * </EmailVerifyGuardTooltip>
 */
const EmailVerifyGuardTooltip = ({
  disabled,
  disabledReason = DEFAULT_DISABLED_REASON,
  children,
}: EmailVerifyGuardTooltipProps) => {
  const { user } = useContext(AppContext);

  if (!disabled) {
    return children;
  }

  // Clone to force the disabled state + a11y attribute onto the control itself.
  const guardedChild = React.cloneElement(children, {
    disabled: true,
    "aria-disabled": true,
  });

  const title = (
    <span>
      {disabledReason}{" "}
      {hasOwnerAccess(user) ? (
        <Link
          component={RouterLink}
          to={COMPANY_PROFILE_PATH}
          color="inherit"
          underline="always"
          sx={{ fontWeight: "bold" }}
        >
          Go to Company Profile
        </Link>
      ) : (
        NON_OWNER_VERIFY_HINT
      )}
    </span>
  );

  return (
    <Tooltip
      title={title}
      arrow
      describeChild
      enterTouchDelay={0}
      leaveTouchDelay={6000}
      PopperProps={POPPER_PROPS}
      slotProps={TOOLTIP_SLOT_PROPS}
    >
      {/* span keeps hover/focus events alive over the disabled control */}
      <span tabIndex={0} style={{ display: "inline-flex" }}>{guardedChild}</span>
    </Tooltip>
  );
};

export default EmailVerifyGuardTooltip;
